import React, { useState, useEffect } from 'react';
import Table from 'react-bootstrap/Table';
import Pagination from 'react-bootstrap/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { InputGroup, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AppNavbar from './AppNavbar';

const API_BASE = 'https://hospital.rozeetechnist.com/hospital-api';

const ExpenseList = () => {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerms, setSearchTerms] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('login')) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    getData();
  }, [currentPage, itemsPerPage, searchTerms, startDate, endDate]);

  const getLoggedUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('login') || '{}');
      return user.id || null;
    } catch (e) {
      return null;
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return '';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getData = () => {
    const loggedUserId = getLoggedUserId();
    fetch(`${API_BASE}/getExpenses.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start: currentPage,
        per_page: itemsPerPage,
        search: searchTerms,
        user_id: loggedUserId,
        start_date: formatDateToYYYYMMDD(startDate) || '',
        end_date: formatDateToYYYYMMDD(endDate) || '',
      }),
    })
      .then((res) => res.json())
      .then((result) => {
        setTotalItems(result.total_records || 0);
        setList(result.data || []);
      })
      .catch((err) => {
        console.error(err);
        setList([]);
        toast.error('Failed to load expenses.');
      });
  };

  const deleteItem = (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    fetch(`${API_BASE}/deleteExpense.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
      .then((res) => res.json())
      .then((resp) => {
        if (resp.status) {
          getData();
          toast.success(resp.message);
        } else {
          toast.error(resp.message || 'Delete failed.');
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to delete.');
      });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const getPaginationItems = () => {
    const items = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, currentPage + 2);
    if (end - start < 4) {
      if (start === 1) end = Math.min(5, totalPages);
      else end = totalPages;
      start = Math.max(1, end - 4);
    }
    for (let i = start; i <= end; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => paginate(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return items;
  };

  return (
    <div>
      <AppNavbar />
      <section className="wrapper">
        <div className="container">
          <div className="row top-page-bar">
            <div className="col-md-4">
              <div className="main-title">
                <h1>Expenses</h1>
              </div>
            </div>
            <div className="col-md-8">
              <div className="search-area">
                <InputGroup>
                  <FormControl
                    placeholder="Search by title, paid to, paid by, notes..."
                    value={searchTerms}
                    onChange={(e) => setSearchTerms(e.target.value)}
                  />
                  <InputGroup.Text></InputGroup.Text>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="From Date"
                    className="form-control"
                    isClearable
                  />
                  <InputGroup.Text></InputGroup.Text>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    dateFormat="dd-MM-yyyy"
                    placeholderText="To Date"
                    className="form-control"
                    isClearable
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        </div>
        <main className="container">
          {list && list.length > 0 ? (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Title</th>
                  <th>Amount</th>
                  <th>Paid To</th>
                  <th>Paid By</th>
                  <th>Notes</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr key={item.id}>
                    <td>{item.expense_date}</td>
                    <td>{item.title}</td>
                    <td>{item.amount}</td>
                    <td>{item.paid_to || '-'}</td>
                    <td>{item.paid_by || '-'}</td>
                    <td>{item.notes ? (item.notes.length > 50 ? item.notes.substring(0, 50) + '...' : item.notes) : '-'}</td>
                    <td className="table-action">
                      <span>
                        <Link to={`/expense-update/${item.id}`}>
                          <FontAwesomeIcon icon={faEdit} color="green" />
                        </Link>
                      </span>
                      <span onClick={() => deleteItem(item.id)}>
                        <FontAwesomeIcon icon={faTrash} color="red" style={{ cursor: 'pointer' }} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : list && list.length === 0 ? (
            <p>No expenses found.</p>
          ) : (
            <p>Loading...</p>
          )}
          {list && list.length > 0 && (
            <Pagination>
              <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1} />
              {getPaginationItems()}
              <Pagination.Next onClick={() => paginate(currentPage + 1)} disabled={currentPage >= totalPages} />
            </Pagination>
          )}
          <div className="createButton">
            <Link to="/expense-create">
              <FontAwesomeIcon icon={faPlus} />
            </Link>
          </div>
        </main>
      </section>
    </div>
  );
};

export default ExpenseList;
